import { Divider } from "@mui/material";
import { TreeItemLabel, TreeViewBaseItem, UseTreeItemStatus } from "@mui/x-tree-view-pro";
import clsx from "clsx";
import { omit } from "ramda";
import React, { MouseEventHandler, type PropsWithChildren, useMemo } from "react";
import { RateBuilderTreeItemContent } from "@sinch/components/application/RatelistBuilder/BuilderTree/components/RateBuilderTreeItemContent";
import type { PositionedItemTree } from "@sinch/components/application/RatelistBuilder/BuilderTree/types";
import { ItemMoveControls } from "@sinch/components/application/RatelistBuilder/RateTreeItem/RowBody/ItemMoveControls/ItemMoveControls";
import {
  ExpandIconSpacer,
  TreeItemExpandIcon,
} from "@sinch/components/application/RatelistBuilder/RateTreeItem/RowBody/TreeItemExpandIcon";
import {
  UseTreeItemIconContainerSlotProps,
  UseTreeItemLabelSlotProps,
  UseTreeViewExpansionPublicAPI,
} from "@sinch/components/application/RatelistBuilder/RateTreeItem/types";
import { useRateBuilderSettings } from "@sinch/components/application/RatelistBuilder/UtilitiesProviders/RateBuilderSettingsProvider";
import { useRegisterForItemSetStyling } from "@sinch/components/providers/ItemSetStylingProvider/ItemSetStylingProvider";

interface RateRowBodyProps<T extends TreeViewBaseItem> extends PropsWithChildren {
  rootRef: React.RefCallback<HTMLDivElement> | null;
  item: PositionedItemTree<T>;
  onContextMenu?: MouseEventHandler<any>;
  isWrapped: boolean;
  status: UseTreeItemStatus;
  iconContainerProps: UseTreeItemIconContainerSlotProps;
  onItemExpandToggle: UseTreeViewExpansionPublicAPI["setItemExpansion"];
  labelProps: UseTreeItemLabelSlotProps;
  draggedHandlerRef: React.RefObject<HTMLDivElement | null>;
}

export const RateRowBody = <T extends TreeViewBaseItem>({
  children,
  iconContainerProps,
  isWrapped,
  item,
  labelProps,
  onContextMenu,
  onItemExpandToggle,
  rootRef,
  status,
  draggedHandlerRef,
}: RateRowBodyProps<T>) => {
  const { mode, disableNesting } = useRateBuilderSettings();
  const itemSetClassNames = useRegisterForItemSetStyling(item.item.id);

  const cleanLabelProps = useMemo(() => omit(["children"], labelProps), [labelProps]);

  const expandIcon = useMemo(
    () =>
      status.expandable ? (
        <TreeItemExpandIcon
          iconContainerProps={iconContainerProps}
          itemId={item.item.id}
          onItemExpandToggle={onItemExpandToggle}
          status={status}
        />
      ) : (
        <ExpandIconSpacer />
      ),
    [status.expandable, iconContainerProps, item.item.id, onItemExpandToggle, status]
  );

  return (
    // @ts-ignore expected error, will be removed in next release
    <RateBuilderTreeItemContent
      ref={rootRef}
      className={clsx(itemSetClassNames)}
      draggable={false}
      onContextMenu={onContextMenu}
    >
      <TreeItemControls
        draggedHandlerRef={draggedHandlerRef}
        isWrapped={isWrapped}
        item={item}
        readOnly={mode === "view" || mode === "override"}
      />

      <TreeItemLabel {...cleanLabelProps}>{children}</TreeItemLabel>

      {!disableNesting && <Divider flexItem orientation="vertical" />}
      {!disableNesting && expandIcon}
    </RateBuilderTreeItemContent>
  );
};

/**
 * Renders the move controls section with appropriate dividers when not in read-only mode.
 */
interface TreeItemControlsProps<T extends TreeViewBaseItem> {
  readOnly: boolean;
  draggedHandlerRef: React.RefObject<HTMLDivElement | null>;
  isWrapped: boolean;
  item: PositionedItemTree<T>;
}

const TreeItemControls: React.FC<TreeItemControlsProps<any>> = ({ readOnly, draggedHandlerRef, isWrapped, item }) => {
  if (readOnly) return null;

  return (
    <>
      <ItemMoveControls dragHandlerRef={draggedHandlerRef} isWrapped={isWrapped} item={item} />
      <Divider flexItem orientation="vertical" />
    </>
  );
};
